import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Fade,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const smsTemplates = [
  "Your OTP is {code}. Please do not share it with anyone.",
  "Dear customer, your appointment is confirmed for {date}.",
  "Thank you for choosing us! Your order #{orderId} is shipped.",
  "Reminder: Your subscription expires on {date}. Renew now!",
  "Hi {name}, welcome to our service! Let us know if you need help."
];

export const SMSCampaign = () => {
  // Tab state
  const [tab, setTab] = useState(0);

  // Run Now state
  const [runNowFile, setRunNowFile] = useState(null);
  const [runNowText, setRunNowText] = useState('');
  const [runNowTemplate, setRunNowTemplate] = useState('');

  // Run Later state
  const [runLaterFile, setRunLaterFile] = useState(null);
  const [runLaterText, setRunLaterText] = useState('');
  const [runLaterTemplate, setRunLaterTemplate] = useState('');
  const [runLaterDateTime, setRunLaterDateTime] = useState(null);

  // Handlers for file upload
  const handleRunNowFileChange = (e) => {
    setRunNowFile(e.target.files[0]);
  };
  const handleRunLaterFileChange = (e) => {
    setRunLaterFile(e.target.files[0]);
  };

  // Handlers for template select
  const handleRunNowTemplateChange = (e) => {
    setRunNowTemplate(e.target.value);
    setRunNowText(e.target.value);
  };
  const handleRunLaterTemplateChange = (e) => {
    setRunLaterTemplate(e.target.value);
    setRunLaterText(e.target.value);
  };

  // Handlers for Run actions (stub)
  const handleRunNow = () => {
    // TODO: Implement API call to send SMS immediately
    alert('Run Now: SMS sent!');
  };
  const handleRunLater = () => {
    // TODO: Implement API call to schedule SMS
    alert('Run Later: SMS scheduled!');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ py: 5, bgcolor: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%)', minHeight: '100vh' }}>
        <Container maxWidth="md">
          {/* Header Section */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#4B5AB6', mb: 0.5 }}>
              SMS Campaign
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 400 }}>
              Send instant or scheduled SMS to your contacts with ease
            </Typography>
          </Box>

          {/* Tabs */}
          <Paper
            elevation={8}
            sx={{
              borderRadius: 4,
              boxShadow: '0 8px 32px rgba(75,90,182,0.16)',
              p: 0,
              maxWidth: 700,
              mx: 'auto',
              overflow: 'hidden',
            }}
          >
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{
                background: '#f8fafc',
                color: '#4B5AB6',
                '& .Mui-selected': { color: '#4B5AB6 !important' },
                '& .MuiTabs-indicator': { backgroundColor: '#4B5AB6' }
              }}
            >
              <Tab
                label="Run Now"
                icon={<SendIcon />}
                iconPosition="start"
                sx={{ fontWeight: 700, fontSize: '1rem' }}
              />
              <Tab
                label="Run Later"
                icon={<ScheduleIcon />}
                iconPosition="start"
                sx={{ fontWeight: 700, fontSize: '1rem' }}
              />
            </Tabs>
            <Divider />
            <Fade in timeout={500}>
              <Box sx={{ p: 4, background: '#fff' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      sx={{
                        bgcolor: '#fff',
                        color: '#4B5AB6',
                        borderColor: '#4B5AB6',
                        fontWeight: 700,
                        borderRadius: 2,
                        '&:hover': { bgcolor: '#eef2ff' }
                      }}
                    >
                      Upload Phone CSV
                      <input type="file" accept=".csv" hidden onChange={tab === 0 ? handleRunNowFileChange : handleRunLaterFileChange} />
                    </Button>
                    {(tab === 0 ? runNowFile : runLaterFile) && (
                      <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>
                        Selected file: {(tab === 0 ? runNowFile : runLaterFile).name}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>SMS Template</InputLabel>
                      <Select
                        value={tab === 0 ? runNowTemplate : runLaterTemplate}
                        label="SMS Template"
                        onChange={tab === 0 ? handleRunNowTemplateChange : handleRunLaterTemplateChange}
                        sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}
                      >
                        {smsTemplates.map((tpl, idx) => (
                          <MenuItem key={idx} value={tpl}>{tpl}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="SMS Text"
                      multiline
                      rows={10}
                      fullWidth
                      value={tab === 0 ? runNowText : runLaterText}
                      onChange={e => tab === 0 ? setRunNowText(e.target.value) : setRunLaterText(e.target.value)}
                      sx={{
                        bgcolor: '#f8fafc',
                        borderRadius: 2,
                        fontSize: '1.1rem',
                        '& .MuiOutlinedInput-root': { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                  {tab === 1 && (
                    <Grid item xs={12}>
                      <DateTimePicker
                        label="Select Date & Time"
                        value={runLaterDateTime}
                        onChange={setRunLaterDateTime}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            sx={{
                              bgcolor: '#f8fafc',
                              borderRadius: 2,
                              '& .MuiOutlinedInput-root': { borderRadius: 2 }
                            }}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Fade>
            <Divider />
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              p: 3,
              background: '#f8fafc',
            }}>
              <Button
                variant="contained"
                startIcon={tab === 0 ? <SendIcon /> : <ScheduleIcon />}
                sx={{
                  background: 'linear-gradient(90deg, #4B5AB6 0%, #6366f1 100%)',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  boxShadow: '0 2px 8px rgba(75,90,182,0.12)',
                  transition: 'transform 0.1s',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #6366f1 0%, #4B5AB6 100%)',
                    transform: 'scale(1.04)',
                  },
                }}
                onClick={tab === 0 ? handleRunNow : handleRunLater}
              >
                {tab === 0 ? 'Send' : 'Schedule'}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default SMSCampaign;